import { makeAutoObservable } from "mobx";
import { round, sortBy, uniqBy } from "lodash";
import { ExtendedShareholderDTO } from "@/types/Shareholder";
import { OwnershipRegistrationMethod, OwnersPerDateDTO, Transaction } from "@/types/Transactions";
import { TMessage } from "@/i18n/tt";
import { NumberFieldModel } from "@/common/commonForms";
import { ShareType } from "@/common/share";
import { ShareTypeRequest } from "@/shareholderRegister/shareClasses/shareClassesDomain";
import { newUuid } from "@/utils/uuid";
import { combineDateAndTime } from "@/utils/Dates";
import { excludedShareholdersFromActors } from "@/stores/ShareholdersStore";
import { SelectedActor } from "@/types/SelectActors";
import { isActorExistingShareholder } from "@/shareholderRegister/transactions/merger/mergerDomain";
import {
  dateBefore,
  TransactionDateCardModel,
} from "@/shareholderRegister/transactions/commonTransactionsDomain";
import { TransactionStore } from "@/stores/TransactionStore";
import { CompanyStatus } from "@/types/CompanyInfo";

export const OWNERSHIP_DECIMALS = 4;

export type RegistrationMethod = "shares" | "percent";

const savedRegistrationMethod = (method?: OwnershipRegistrationMethod): RegistrationMethod =>
  !method || method === "SHARES" ? "shares" : "percent";

export function ownershipPercent(shares: number, total: number): number {
  return total > 0 ? round((shares / total) * 100, OWNERSHIP_DECIMALS) : 0;
}

export function percentDecimals(percent: number): number {
  const fraction = percent.toFixed(OWNERSHIP_DECIMALS).split(".")[1] ?? "";
  return fraction.replace(/0+$/, "").length;
}

export type ShareClassTotalCallbacks = {
  onTotalChange: () => void;
  onVotesChange: () => void;
};

export class ShareClassTotalModel {
  shareType: ShareType;
  totalNumberOfShares: NumberFieldModel;
  votes: NumberFieldModel;

  constructor(shareType: ShareType, callbacks: ShareClassTotalCallbacks, total?: number) {
    this.shareType = shareType;
    this.totalNumberOfShares = new NumberFieldModel({
      onChange: callbacks.onTotalChange,
      value: total,
      isPositiveOrZero: true,
    });
    this.votes = new NumberFieldModel({
      onChange: callbacks.onVotesChange,
      value: shareType.vote,
      isPositiveOrZero: true,
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get isVotesChanged(): boolean {
    return this.votes.toNumberOrZero() !== this.shareType.vote;
  }
}

export type OwnerRowCallbacks = {
  setDirty: () => void;
  classTotal: (sharetypeId: number) => number;
  enteredTotal: (sharetypeId: number) => number;
  isAutomaticStructure: () => boolean;
};

export class OwnerRowModel {
  id = newUuid();
  shareholder: ExtendedShareholderDTO;
  sharesByClass = new Map<number, NumberFieldModel>();
  percentByClass = new Map<number, NumberFieldModel>();
  private isDerivingPercent = false;

  constructor(
    shareholder: ExtendedShareholderDTO,
    shareTypes: ShareType[],
    readonly callbacks: OwnerRowCallbacks,
    sharesByClass?: Map<number, number>,
  ) {
    this.shareholder = shareholder;
    shareTypes.forEach((st) =>
      this.addShareClass(st.sharetypeId, sharesByClass?.get(st.sharetypeId)),
    );
    makeAutoObservable(this, { callbacks: false }, { autoBind: true });
  }

  addShareClass(sharetypeId: number, shares?: number) {
    this.sharesByClass.set(
      sharetypeId,
      new NumberFieldModel({
        onChange: () => this.onSharesChanged(sharetypeId),
        value: shares,
        isPositiveOrZero: true,
      }),
    );
    this.percentByClass.set(
      sharetypeId,
      new NumberFieldModel({
        onChange: () => this.onPercentChanged(sharetypeId),
        value:
          shares === undefined
            ? undefined
            : ownershipPercent(shares, this.callbacks.enteredTotal(sharetypeId)),
        isPositiveOrZero: true,
        minDecimals: OWNERSHIP_DECIMALS,
        maxDecimals: OWNERSHIP_DECIMALS,
        validator: (value) =>
          value !== undefined && value > 100
            ? {
                key: "number-field.smaller-or-equal-than-n",
                params: { n: { type: "number", value: 100 } },
              }
            : undefined,
      }),
    );
  }

  shares(sharetypeId: number): number {
    return this.sharesByClass.get(sharetypeId)?.toNumberOrZero() ?? 0;
  }

  percent(sharetypeId: number): number {
    return this.percentByClass.get(sharetypeId)?.toNumberOrZero() ?? 0;
  }

  syncPercentFromShares(sharetypeId: number) {
    const percentField = this.percentByClass.get(sharetypeId);
    if (!percentField) return;
    const percent = ownershipPercent(
      this.shares(sharetypeId),
      this.callbacks.classTotal(sharetypeId),
    );
    if (percentField.toNumberOrZero() === percent) return;
    this.isDerivingPercent = true;
    try {
      percentField.setAndCommitInputValue(percent);
    } finally {
      this.isDerivingPercent = false;
    }
  }

  private onSharesChanged(sharetypeId: number) {
    this.callbacks.setDirty();
    this.syncPercentFromShares(sharetypeId);
  }

  private onPercentChanged(sharetypeId: number) {
    if (this.isDerivingPercent) return;
    this.callbacks.setDirty();
    if (this.callbacks.isAutomaticStructure()) return;
    this.syncSharesFromPercent(sharetypeId);
  }

  syncSharesFromPercent(sharetypeId: number) {
    const sharesField = this.sharesByClass.get(sharetypeId);
    if (!sharesField) return;
    const shares = Math.round(
      (this.percent(sharetypeId) / 100) * this.callbacks.classTotal(sharetypeId),
    );
    if (sharesField.toNumberOrZero() !== shares) {
      sharesField.setAndCommitInputValue(shares);
    }
    this.syncPercentFromShares(sharetypeId);
  }
}

function sharesFromRegister(shareholder: ExtendedShareholderDTO, shareTypes: ShareType[]) {
  return new Map(
    shareTypes.map((st) => [st.sharetypeId, shareholder.calculatedShares[st.shareTypeCode] ?? 0]),
  );
}

export class OwnersPerDateModel {
  transactionDateCardModel: TransactionDateCardModel;
  readonly nominalValue: number;
  registrationMethod: RegistrationMethod;
  knowsShareStructure: boolean;
  companyStatus?: CompanyStatus;
  shareClassTotals: ShareClassTotalModel[] = [];
  owners: OwnerRowModel[] = [];
  knownShareholders: ExtendedShareholderDTO[] = [];
  error?: TMessage;
  isDirty = false;
  hasUnknownOwners = false;

  constructor(
    readonly transactionStore: TransactionStore,
    readonly companyData: { orgNumber: string },
    readonly shareholderData: {
      activeShareTypes: ShareType[];
      shareholders: ExtendedShareholderDTO[];
      transactionLog: Transaction[];
      loadShareholdersForDate: (orgNumber: string, date: string) => unknown;
    },
    readonly transactionData?: OwnersPerDateDTO,
  ) {
    this.transactionDateCardModel = new TransactionDateCardModel({
      data: shareholderData,
      value: transactionData?.transactionDate,
      allowAnyDate: true,
      onChange: (date, time) => {
        this.setDirty();
        if (date && time) {
          this.fetchState(combineDateAndTime(date, time));
        }
      },
    });
    this.nominalValue = transactionData?.nominalValue ?? 1;
    this.registrationMethod = transactionData
      ? savedRegistrationMethod(transactionData.registrationMethod)
      : "percent";
    this.knowsShareStructure = transactionData?.registrationMethod === "PERCENT";
    this.companyStatus = transactionData?.companyStatus;
    makeAutoObservable(
      this,
      { transactionStore: false, companyData: false, shareholderData: false },
      { autoBind: true },
    );
  }

  get shareTypes(): ShareType[] {
    return this.shareholderData.activeShareTypes;
  }

  *fetchState(date: string) {
    yield this.shareholderData.loadShareholdersForDate(
      this.companyData.orgNumber,
      dateBefore(date),
    );
    if (this.transactionData) {
      this.initFromTransaction(this.transactionData);
    } else {
      this.initFromRegister();
    }
  }

  private initFromTransaction(data: OwnersPerDateDTO) {
    const shareTypes = this.shareTypes;
    this.shareClassTotals = shareTypes.map(
      (st) =>
        new ShareClassTotalModel(
          st,
          this.shareClassTotalCallbacks(st.sharetypeId),
          data.shareClassTotals.find((t) => t.sharetypeId === st.sharetypeId)?.totalNumberOfShares,
        ),
    );
    const byShareholder = new Map<number, Map<number, number>>();
    data.shareAllocation.forEach((a) => {
      const shares = byShareholder.get(a.shareholderId) ?? new Map<number, number>();
      shares.set(a.sharetypeId, a.numberOfShares);
      byShareholder.set(a.shareholderId, shares);
    });
    const knownShareholderIds = new Set(this.shareholderData.shareholders.map((s) => s.id));
    this.hasUnknownOwners = [...byShareholder.keys()].some((id) => !knownShareholderIds.has(id));
    this.owners = sortBy(
      this.shareholderData.shareholders.filter((s) => byShareholder.has(s.id)),
      "name",
    ).map((s) => new OwnerRowModel(s, shareTypes, this.ownerRowCallbacks, byShareholder.get(s.id)));
  }

  private initFromRegister() {
    const shareTypes = this.shareTypes;
    const identified = this.shareholderData.shareholders.filter((s) => s.isUnidentified !== true);
    const all = this.shareholderData.shareholders;
    this.shareClassTotals = shareTypes.map((st) => {
      const total = all.reduce((sum, s) => sum + (s.calculatedShares[st.shareTypeCode] ?? 0), 0);
      return new ShareClassTotalModel(
        st,
        this.shareClassTotalCallbacks(st.sharetypeId),
        total > 0 ? total : undefined,
      );
    });
    this.owners = sortBy(identified, "name").map(
      (s) =>
        new OwnerRowModel(s, shareTypes, this.ownerRowCallbacks, sharesFromRegister(s, shareTypes)),
    );
  }

  get isPercentMethod(): boolean {
    return this.registrationMethod === "percent";
  }

  get ownershipRegistrationMethod(): OwnershipRegistrationMethod {
    if (!this.isPercentMethod) return "SHARES";
    return this.knowsShareStructure ? "PERCENT" : "PERCENT_WITH_AUTOMATIC_STRUCTURE";
  }

  get isAutomaticStructure(): boolean {
    return this.isPercentMethod && !this.knowsShareStructure;
  }

  get isShareStructureEntered(): boolean {
    return !this.isPercentMethod || this.knowsShareStructure;
  }

  setRegistrationMethod(method: RegistrationMethod) {
    this.changeMode(method, this.knowsShareStructure);
  }

  setCompanyStatus(status: CompanyStatus) {
    this.companyStatus = status;
    this.setDirty();
  }

  setKnowsShareStructure(knows: boolean) {
    this.changeMode(this.registrationMethod, knows);
  }

  private changeMode(method: RegistrationMethod, knows: boolean) {
    if (method === this.registrationMethod && knows === this.knowsShareStructure) return;
    const staysAutomatic = method === "percent" && knows === false;
    if (this.isAutomaticStructure && !staysAutomatic) {
      this.shareClassTotals.forEach(this.applyDerivedShareStructure);
    }
    this.registrationMethod = method;
    this.knowsShareStructure = knows;
    this.setDirty();
  }

  private applyDerivedShareStructure(classTotal: ShareClassTotalModel) {
    const sharetypeId = classTotal.shareType.sharetypeId;
    const total = this.derivedTotal(sharetypeId);
    if (total === 0) return;
    this.owners.forEach((o) =>
      o.sharesByClass.get(sharetypeId)?.setAndCommitInputValue(this.derivedShares(o, sharetypeId)),
    );
    classTotal.totalNumberOfShares.setAndCommitInputValue(total);
  }

  private derivedDecimals(sharetypeId: number): number {
    return Math.max(0, ...this.owners.map((o) => percentDecimals(o.percent(sharetypeId))));
  }

  derivedTotal(sharetypeId: number): number {
    return this.hasNoOwnership || this.owners.some((o) => o.percent(sharetypeId) > 0)
      ? 10 ** (this.derivedDecimals(sharetypeId) + 2)
      : 0;
  }

  private derivedShares(owner: OwnerRowModel, sharetypeId: number): number {
    return Math.round(owner.percent(sharetypeId) * 10 ** this.derivedDecimals(sharetypeId));
  }

  ownerShares(owner: OwnerRowModel, sharetypeId: number): number {
    return this.isAutomaticStructure
      ? this.derivedShares(owner, sharetypeId)
      : owner.shares(sharetypeId);
  }

  total(sharetypeId: number): number {
    return this.isAutomaticStructure
      ? this.derivedTotal(sharetypeId)
      : this.enteredTotal(sharetypeId);
  }

  private enteredTotal(sharetypeId: number): number {
    return (
      this.shareClassTotals
        .find((t) => t.shareType.sharetypeId === sharetypeId)
        ?.totalNumberOfShares.toNumberOrZero() ?? 0
    );
  }

  allocated(sharetypeId: number): number {
    return this.owners.reduce((sum, o) => sum + this.ownerShares(o, sharetypeId), 0);
  }

  get hasNoOwnership(): boolean {
    return this.shareClassTotals.every((t) => this.allocated(t.shareType.sharetypeId) === 0);
  }

  remainder(sharetypeId: number): number {
    return this.total(sharetypeId) - this.allocated(sharetypeId);
  }

  get hasNegativeRemainder(): boolean {
    return this.shareClassTotals.some((t) => this.remainder(t.shareType.sharetypeId) < 0);
  }

  remainderPercent(sharetypeId: number): number {
    return ownershipPercent(this.remainder(sharetypeId), this.total(sharetypeId));
  }

  private shareClassTotalCallbacks(sharetypeId: number): ShareClassTotalCallbacks {
    return {
      onTotalChange: () => {
        this.setDirty();
        this.owners.forEach((o) =>
          this.isPercentMethod
            ? o.syncSharesFromPercent(sharetypeId)
            : o.syncPercentFromShares(sharetypeId),
        );
      },
      onVotesChange: this.setDirty,
    };
  }

  private get ownerRowCallbacks(): OwnerRowCallbacks {
    return {
      setDirty: this.setDirty,
      classTotal: (sharetypeId) => this.total(sharetypeId),
      enteredTotal: (sharetypeId) => this.enteredTotal(sharetypeId),
      isAutomaticStructure: () => this.isAutomaticStructure,
    };
  }

  changedVotes(): ShareClassTotalModel[] {
    return this.shareClassTotals.filter((t) => t.isVotesChanged);
  }

  shareTypeRequest(total: ShareClassTotalModel): ShareTypeRequest {
    return {
      organizationnumber: this.companyData.orgNumber,
      vote: total.votes.toNumberOrZero(),
      shareTypeCode: total.shareType.shareTypeCode,
      shareTypeNames: total.shareType.shareTypeNames,
    };
  }

  addOwner(shareholder: ExtendedShareholderDTO) {
    this.knownShareholders = uniqBy([...this.knownShareholders, shareholder], (s) => s.id);
    if (this.owners.some((o) => o.shareholder.id === shareholder.id)) return;
    this.owners = [
      ...this.owners,
      new OwnerRowModel(shareholder, this.shareTypes, this.ownerRowCallbacks),
    ];
    this.setDirty();
  }

  addNewSubscribers(shareholders: ExtendedShareholderDTO[], onSuccess: () => void) {
    shareholders.forEach((s) => this.addOwner(s));
    onSuccess();
  }

  addFromActors(
    selectedActors: SelectedActor[],
    createShareholders: (
      actors: SelectedActor[],
      onSuccess: (shareholders: ExtendedShareholderDTO[]) => void,
      onError: () => void,
    ) => void,
    onSuccess: () => void,
    onError: () => void,
  ) {
    const known = [...this.shareholderData.shareholders, ...this.knownShareholders];
    const matches = selectedActors.map((actor) => ({
      actor,
      shareholder: known.find((s) => isActorExistingShareholder(actor, s)),
    }));
    matches.forEach((m) => m.shareholder && this.addOwner(m.shareholder));
    const unknownActors = matches.filter((m) => m.shareholder === undefined).map((m) => m.actor);
    if (unknownActors.length === 0) {
      onSuccess();
      return;
    }
    createShareholders(
      unknownActors,
      (shareholders) => this.addNewSubscribers(shareholders, onSuccess),
      onError,
    );
  }

  removeOwner(id: string) {
    this.owners = this.owners.filter((o) => o.id !== id);
    this.setDirty();
  }

  onNewShareTypeAdded(shareType: ShareType) {
    this.shareClassTotals = [
      ...this.shareClassTotals,
      new ShareClassTotalModel(shareType, this.shareClassTotalCallbacks(shareType.sharetypeId)),
    ];
    this.owners.forEach((o) => o.addShareClass(shareType.sharetypeId));
    this.setDirty();
  }

  get excludedShareholders(): string[] {
    return excludedShareholdersFromActors(this.owners.map((o) => o.shareholder));
  }

  get isValid(): boolean {
    return (
      !this.error &&
      this.transactionDateCardModel.isValid &&
      this.shareClassTotals.every((t) => t.votes.isValid) &&
      this.owners.every((o) => [...o.percentByClass.values()].every((f) => f.isValid))
    );
  }

  validate(): boolean {
    this.error = undefined;
    this.transactionDateCardModel.validate();
    this.shareClassTotals.forEach((t) => {
      t.totalNumberOfShares.validate();
      t.votes.validate();
    });
    this.owners.forEach((o) => {
      o.sharesByClass.forEach((f) => f.validate());
      o.percentByClass.forEach((f) => f.validate());
    });

    if (this.hasUnknownOwners) {
      this.error = { key: "transactions.ownersPerDate.unknownOwner" };
    } else if (
      !this.isAutomaticStructure &&
      (this.shareClassTotals.length === 0 ||
        this.shareClassTotals.every((t) => t.totalNumberOfShares.toNumberOrZero() <= 0))
    ) {
      this.error = { key: "transactions.ownersPerDate.missingTotal" };
    } else if (this.hasNoOwnership && !this.companyStatus) {
      this.error = { key: "transactions.ownersPerDate.missingCompanyStatus" };
    } else if (this.hasNegativeRemainder) {
      this.error = { key: "transactions.ownersPerDate.negativeRemainder" };
    }
    return this.isValid;
  }

  prepareData(): OwnersPerDateDTO {
    return {
      organizationnumber: this.companyData.orgNumber,
      transactionDate: this.transactionDateCardModel.combinedDateAndTime ?? "",
      nominalValue: this.nominalValue,
      companyStatus: this.hasNoOwnership ? this.companyStatus : undefined,
      registrationMethod: this.ownershipRegistrationMethod,
      shareClassTotals: this.shareClassTotals
        .map((t) => ({
          sharetypeId: t.shareType.sharetypeId,
          totalNumberOfShares: this.total(t.shareType.sharetypeId),
        }))
        .filter((t) => t.totalNumberOfShares > 0),
      shareAllocation: this.owners.flatMap((o) =>
        [...o.sharesByClass.keys()]
          .map((sharetypeId) => ({
            shareholderId: o.shareholder.id,
            sharetypeId,
            numberOfShares: this.ownerShares(o, sharetypeId),
          }))
          .filter((a) => a.numberOfShares > 0),
      ),
    };
  }

  setDirty() {
    this.isDirty = true;
  }

  clearDirty() {
    this.isDirty = false;
  }
}
